import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { usePortalDismiss } from '../hooks/usePortalDismiss'
import { CHART_COLORS } from '../utils/chartColors'
import { ChartLayoutPicker, type ChartLayout } from './ChartLayoutPicker'
import {
  ChartSyncToggles,
  type ChartSyncTogglesValue,
  type SyncToggleLocks,
  type SyncTogglesKey,
} from './ChartSyncToggles'
import { Z_PANEL } from './zLayers'

const C = {
  popover: CHART_COLORS.surface,
  border: CHART_COLORS.border,
  text: CHART_COLORS.text,
  textSecond: CHART_COLORS.textMuted,
}

interface ChartSettingsPopoverProps {
  layout: ChartLayout
  onLayoutChange: (layout: ChartLayout) => void
  syncValue: ChartSyncTogglesValue
  onSyncChange: (next: ChartSyncTogglesValue) => void
  syncLocks?: SyncToggleLocks
  /** Sync rows to leave out (see ChartSyncToggles); anchored grids pass `[]` to offer Visible window. */
  hiddenSyncToggles?: readonly SyncTogglesKey[]
  presets?: readonly ChartLayout[]
  /** Controlled open state. If omitted, the popover manages its own. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /**
   * Viewport point of the Layout-button click. The popover anchors beside it —
   * portaled + `position: fixed` — exactly like the chart-settings modal and
   * indicator drawer. Without it, the popover falls back to the top-right of the
   * viewport.
   */
  anchor?: { x: number; y: number }
  /** @deprecated Superseded by `anchor` — accepted but ignored now the popover
   *  is a portaled, click-anchored popup. */
  anchorStyle?: CSSProperties
  /**
   * 'chart' (default): chart-styled surface, for the in-chart toolbar chip.
   * 'panel': matches the workspace panel-header dropdowns (bg-popover tokens,
   * rounded-md, app font) - used when the header Layout action opens it.
   */
  variant?: 'chart' | 'panel'
}

/**
 * Chart-level settings: layout presets + "Sync in layout" toggles. A portaled,
 * click-anchored popup (matching the settings modal + indicator drawer):
 * `position: fixed` beside the Layout-button click, dismissed on Escape or an
 * outside click. Owned by the chart package so the chart fully owns its surface.
 */
export function ChartSettingsPopover({
  layout,
  onLayoutChange,
  syncValue,
  onSyncChange,
  syncLocks,
  hiddenSyncToggles,
  presets,
  open: openProp,
  onOpenChange,
  anchor,
  variant = 'chart',
}: ChartSettingsPopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const open = openProp ?? uncontrolledOpen
  const contentRef = useRef<HTMLDivElement>(null)

  const setOpen = (next: boolean) => {
    if (openProp === undefined) setUncontrolledOpen(next)
    onOpenChange?.(next)
  }

  // Two-pass placement: render hidden, measure, then position as a DROPDOWN
  // anchored to the Layout-button click — top-aligned to the click so it opens
  // downward (this panel is tall; vertically centering it, like the settings
  // modal, would clamp a top-toolbar click to the very top edge). The right edge
  // sits at the click so it opens leftward from a top-right toolbar button.
  // Both axes are clamped to the viewport; falls back to top-right when opened
  // without a click anchor.
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)
  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    const el = contentRef.current
    if (!el) return
    const w = el.offsetWidth
    const h = el.offsetHeight
    const m = 8
    if (anchor) {
      const left = Math.max(m, Math.min(anchor.x - w, window.innerWidth - w - m))
      const top = Math.max(m, Math.min(anchor.y, window.innerHeight - h - m))
      setPos({ left, top })
    } else {
      setPos({ left: Math.max(m, window.innerWidth - w - 16), top: 52 })
    }
  }, [open, anchor])

  // Escape + outside-click dismiss (only while open). The trigger buttons and
  // nested Radix poppers (sync info tooltips) are guarded so clicking them
  // doesn't bounce the popover closed.
  usePortalDismiss(contentRef, () => setOpen(false), {
    enabled: open,
    guardSelector: '[data-action="layout"], [data-action="settings"]',
  })

  if (!open) return null

  const isPanel = variant === 'panel'
  return createPortal(
    <div
      ref={contentRef}
      data-chart-popup=""
      data-testid="chart-settings-popover"
      // Panel variant mirrors the workspace PopoverContent look (bg-popover
      // tokens, rounded-md, soft shadow, app font) so the header Layout
      // dropdown matches its sibling top-bar dropdowns.
      className={
        isPanel
          ? 'rounded-md border border-primary/15 bg-popover text-popover-foreground shadow-2xl shadow-black/40'
          : undefined
      }
      style={{
        position: 'fixed',
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        visibility: pos ? 'visible' : 'hidden',
        zIndex: Z_PANEL,
        width: 208,
        padding: 4,
        ...(isPanel
          ? {}
          : {
              background: C.popover,
              border: `1px solid ${C.border}`,
              borderRadius: 4,
              color: C.text,
              boxShadow: '0 6px 12px 0 rgba(0,0,0,.3)',
            }),
      }}
    >
      <SectionLabel isPanel={isPanel}>Layout</SectionLabel>
      <div style={{ padding: 8 }}>
        <ChartLayoutPicker layout={layout} onLayoutChange={onLayoutChange} presets={presets} />
      </div>
      <SectionLabel isPanel={isPanel} topBorder>
        Sync in layout
      </SectionLabel>
      <div style={{ padding: '4px 4px 8px' }}>
        <ChartSyncToggles
          value={syncValue}
          onChange={onSyncChange}
          locks={syncLocks}
          hiddenToggles={hiddenSyncToggles}
        />
      </div>
    </div>,
    document.body,
  )
}

function SectionLabel({
  children,
  topBorder,
  isPanel,
}: {
  children: React.ReactNode
  topBorder?: boolean
  isPanel?: boolean
}) {
  return (
    <div
      className={isPanel ? 'text-muted-foreground' : undefined}
      style={{
        padding: '6px 8px',
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: isPanel ? undefined : C.textSecond,
        borderTop: topBorder ? `1px solid ${isPanel ? 'hsl(var(--border))' : C.border}` : undefined,
        borderBottom: topBorder
          ? undefined
          : `1px solid ${isPanel ? 'hsl(var(--border))' : C.border}`,
      }}
    >
      {children}
    </div>
  )
}
